import { useState, useEffect } from "react";
import { Typography, Box, Link, Skeleton,} from "@mui/material";
import { Translate, translate } from "react-i18nify";
import { PatientDetailText, PatientDetailTextWarning} from "./PatientDetailText";
import PatientDashboardCategory from "./PatientDashboardCategory";
import PatientDashboardCategoryWarning from "./PatientDashboardCategoryWarning";
import TimeUtils from "../../utils/TimeUtils";
import { PatientService, SessionService } from "../../services/DataService";
import { DeviceService } from "../../services/DataService";
import { EventLogService } from "../../services/DataService";
import PatientUtils from "../../utils/PatientUtil";



const PatientDeviceInformation = ({patient, device, locationMismatch, awaitingDevice, installerView}) => {
    const [deviceMac, setDeviceMac] = useState(null);
    const [deviceUdsn, setDeviceUdsn] = useState(null);
    const [latestDeviceLog, setLatestDeviceLog] = useState(null);
    const [latestSession, setLatestSession] = useState(null);
    const [patientComponent, setPatientComponent] = useState(null);
    const [deviceLocation, setDeviceLocation] = useState(null);
    const [awaitingLastSession, setAwaitingLastSession] = useState(true);
    const [awaitingLastLog, setAwaitingLastLog] = useState(true);

    useEffect(() => {
      setDeviceUdsn(device?.metaData.find(metadata => metadata.name === "Serial Number")?.value)
      setDeviceLocation(device?.location.name)
      if(!awaitingDevice){
        if(device){
          (async () => {
            await EventLogService.getLatestByUdsn(device?.metaData[0].value).then(response => {
              setLatestDeviceLog(response?.data)
            }).catch(error => {
              console.error("unable to fetch latest device log\n", error)
            }).finally(() => {
              setAwaitingLastLog(false)
              if(patient == null)
                setAwaitingLastSession(false)
            })
          })();
          if(!installerView){
            (async () => {
              await DeviceService.getHotByUdsn(device?.metaData[0].value).then(response => {
                setDeviceMac(response.data.mac)  
              }).catch(error => {
                console.error("unable to fetch MAC address\n", error)
                setDeviceMac("-");
              });
            })();
          } else if(installerView){
            setDeviceMac(device.children.find(c => c.name === "Hot Chassis").children.find(c => c.name === "O2matic Hot Print").metaData.find(c => c.metadataId === 16).value)
          }
        }else{
          setDeviceUdsn("PatientNotAssignedToDevice"); 
          setDeviceMac("-");
          setDeviceLocation("-"); 
          setAwaitingLastLog(false)
        }
      }
    },[device, awaitingDevice]);

  
    useEffect(() => {
      if(patient?.patient){
        if(!latestSession){
          (async () => {
            try{
              let apiLatestSession = await SessionService.getLatestByPatientId(patient.patient.id.value);
              setLatestSession(apiLatestSession.data);       
            }catch(error){
              console.error("unable to fetch latest session\n" + error.message)
            }finally{
              setAwaitingLastSession(false)
            }
          })();
        }
        if(device)
          (async () => {
            await PatientService.getPatientComponent(patient?.patient.id.value, device?.id).then((response) => {
              setPatientComponent(response.data)
            }).catch((error) => {
              console.error("unable to fetch Patient component\n", error)
              setPatientComponent("-")
            });
          })();
      }else{
        setPatientComponent("-")
        setAwaitingLastSession(false)
        // setLatestSession("-")
      }
    }, [patient, device])
    
    return (

      <Box
        sx={{
          // width: "70%",
          width: { xs: "100%", sm: "100%", md: "100%", lg: "1000px" },
          maxWidth: {md: "1000px"},
          border: 3,
          borderRadius: "15px", border: 1, borderColor: "primary.main", bgcolor: "primary.main", color: "primary.contrastText",
          display: "flex",
          // flexDirection: { xs: "column", md: "row" },
        }}
      >

        <Box sx={{ width:"100%", m: 1, p: 1 }}>
          <Typography sx={{ mt: 1 }} variant="body">
              {<Translate value="DeviceInformation"/>}
          </Typography>

          { !deviceUdsn ? 
            <Skeleton variant="rounded" height={45} width={"40%"} sx={{ mt: 1}} />
          :         
            deviceUdsn == "PatientNotAssignedToDevice" ?      
            <PatientDashboardCategoryWarning
              title={translate(deviceUdsn)}
            />
            :
            <>
            <PatientDashboardCategory
              title={ <Link href={"device/HOT/" + deviceUdsn} color="inherit" underline="hover">{deviceUdsn} </Link>}
            />

            <Box sx={{width:"100%", display:"flex", flexDirection:{xs: "column", sm: "row"}, justifyContent: 'space-between'}}>
              <Box sx={{width:{xs:"100%", sm:"45%"}}}>
                <Box sx={{ width:"100%", mt: 2}}>
                {deviceMac && deviceLocation && patientComponent ? (
                  <>
                    {locationMismatch? // add warning color to location if there is a mismatch between patient location and device location
                      <PatientDetailTextWarning
                        label={translate("Location")}
                        value={deviceLocation + " (" + translate("locationsDoNotMatch") + ")"}
                      ></PatientDetailTextWarning>
                      :
                      <PatientDetailText
                      label={translate("Location")}
                      value={deviceLocation}
                      />
                    }
                    <PatientDetailText
                      label={translate("MacAddress")}
                      value={deviceMac}
                      />
                    {installerView === true ? 
                    <>
                      <PatientDetailText
                      label={<Translate value="SoftwareVersion" />}
                      value={
                        device?.metaData?.find(md => md.name == "Hot_Software_version").value ? device?.metaData?.find(md => md.name == "Hot_Software_version").value : "-"
                        
                        }
                      />
                      <PatientDetailText
                        label={<Translate value="FirmwareVersion" />}
                        value={
                          device?.metaData?.find(md => md.name == "Hot_Firmware_Version").value ? device?.metaData?.find(md => md.name == "Hot_Firmware_Version").value : "-"
                        }
                      />
                    </>
                    :
                    <></>
                    }
                    
                    <PatientDetailText
                      label={translate("DeviceAssignedToPatient")}
                      value={ patientComponent && patientComponent !== "-" ? TimeUtils.utcToLocal(patientComponent.start) : translate(patientComponent)}
                    />
                  </>
                )
                :
                <Skeleton variant="rounded" height={73} width={"90%"} />
                }
                </Box>

                <Box sx={{ width:"100%", mt: 2}}>
                {!awaitingLastSession && !awaitingLastLog ?
                  <>
                  {(latestDeviceLog === null)? 
                  <PatientDetailText
                    label={translate("LatestReceivedDeviceLog")}
                    value={"-"}
                  ></PatientDetailText>
                  : 
                  <PatientDetailText
                    label={translate("LatestReceivedDeviceLog")}
                    value={TimeUtils.utcToLocal(latestDeviceLog)}
                  ></PatientDetailText>}
                  {(!latestSession)?  //Maybe shouldn't be here for installer view?
                  <PatientDetailText
                    label={translate("LastSession")}
                    value={"-"}
                  ></PatientDetailText>
                  : 
                  <PatientDetailText
                    label={translate("LastSession")}
                    value={TimeUtils.utcToLocal(latestSession?.timeStamp)}
                  ></PatientDetailText>}
                  {(!latestSession || installerView)? 
                  <PatientDetailText
                    label={translate("NextSession")}
                    value={"-"}
                  ></PatientDetailText>
                  : 
                  
                  <PatientDetailText
                    label={translate("NextSession")}
                    value={TimeUtils.formatDate((TimeUtils.addSeconds(TimeUtils.utcToLocalDate(latestSession?.timeStamp),parseInt(latestSession?.value))))}
                  ></PatientDetailText>}
                  </>
                :
                  <Skeleton variant="rounded" height={73} width={"90%"} />
                }
                </Box>
              </Box>

              <Box sx={{width:{xs:"100%", sm:"45%"}}}>
              {patient == null && patientComponent == null ? 
                <Skeleton variant="rounded" height={49} width={"70%"}  sx={{mt:2}} />

              :
                <Box sx={{mt: 1}}>
                  <PatientDetailText
                    label={<Translate value="ServiceProvider" />}
                    value={
                      PatientUtils.getContactString(patient?.patient?.contact,"Installer").name? PatientUtils.getContactString(patient?.patient?.contact,"Installer").name : "-"
                    }
                  ></PatientDetailText>
                  <PatientDetailText
                    label={<Translate value="PhoneNumber" />}
                    value={
                      PatientUtils.getContactString(patient?.patient?.contact,"Installer").phone? PatientUtils.getContactString(patient?.patient?.contact,"Installer").phone : "-"
                    }
                  ></PatientDetailText>
                </Box>
              }
              {patient == null && patientComponent == null ? 
                <Skeleton variant="rounded" height={49} width={"70%"}  sx={{mt:2}} />
              :
                <Box sx={{mt: 3}}> 
                    <PatientDetailText
                      label={<Translate value="HealthcareProvider" />}
                      value={
                        PatientUtils.getContactString(patient?.patient?.contact,"Healthcare").name? PatientUtils.getContactString(patient?.patient?.contact,"Healthcare").name : "-"
                      }
                    />
                    <PatientDetailText
                      label={<Translate value="PhoneNumber" />}
                      value={
                        PatientUtils.getContactString(patient?.patient?.contact,"Healthcare").phone? PatientUtils.getContactString(patient?.patient?.contact,"Healthcare").phone : "-"
                      }
                    />
                  </Box>
              }
            </Box>
          </Box>
          </>
          }        
        </Box>

      </Box>


    );
};

export default PatientDeviceInformation;